<template>
    <component :is="callback ? 'button' : 'router-link'" v-if="item" :to="path" data-cy="menu-item" class="no-underline" :id="'item-' + createSlug(item.name)" @click="itemClicked">
        <ion-card button class="my-0 mx-0 full-height">
            <!-- Desktop View -->
            <template v-if="!isMobile">
                <div class="img-banner py-4" v-if="detail && item.itemTextBanner" data-cy="menu-item-banner">
                    {{ item.itemTextBanner }}
                </div>
                <ion-img v-if="item.img" :src="require('@/assets/menu/' + item.img)" alt="" :class="imageClass" data-cy="menu-image" />
                <ion-img v-else-if="item.cdnImg" :src="item.cdnImg" alt="" :class="imageClass" data-cy="menu-image" />
                <ion-item :detail="isMobile" class="interstate-bold" lines="none" data-cy="menu-info">
                    <ion-label>
                        <div v-html="item.messageText ?? item.name" />
                        <div v-if="route.path.includes('pizza-pals') && groupNumber == 1">
                            <div v-if="item.categoryName === 'Pizza'" class="small normal">{{ item.recipes[0].sizes[0].sizeName }}</div>
                            <div v-else>&nbsp;</div>
                        </div>
                    </ion-label>
                </ion-item>
            </template>
            <!-- End Desktop View -->

            <!-- Mobile View -->
            <ion-row v-if="isMobile" class="ion-align-items-center">
                <div class="img-banner py-4" v-if="detail && item.itemTextBanner" data-cy="menu-item-banner">
                    {{ item.itemTextBanner }}
                </div>

                <ion-col class="px-0 py-0 img-col" size="5" :class="{ detailImg: detail }" data-cy="menu-image">
                    <ion-img v-if="item.img" :src="require('@/assets/menu/' + item.img)" alt="" :class="imageClass" />
                    <ion-img v-else-if="item.cdnImg" :src="isMobile ? item.mobileImg : item.cdnImg" alt="" :class="imageClass" />
                </ion-col>

                <ion-col class="px-0 py-0 item-detail-col" size="7" :class="{ detailInfo: detail }" data-cy="menu-info">
                    <ion-item :detail="isMobile" class="interstate-bold" lines="none">
                        <ion-label>
                            <div v-html="item.messageText ?? item.name" />
                            <div v-if="route.path.includes('pizza-pals') && groupNumber == 1 && item.categoryName === 'Pizza'" class="small normal">{{ item.recipes[0].sizes[0].sizeName }}</div>
                        </ion-label>
                    </ion-item>
                </ion-col>
            </ion-row>
            <!-- End Mobile View -->
        </ion-card>
    </component>
</template>
<script lang="ts">
import { computed, defineComponent, PropType, toRef, watchEffect } from 'vue';
import { IonCard, IonCol, IonImg, IonItem, IonRow, IonLabel } from '@ionic/vue';
import { MenuItem } from '@/models/menuModels';
import { useStore } from 'vuex';
import { createSlug } from '@/services/stringService';
import { useRoute } from 'vue-router';

export default defineComponent({
    props: {
        item: { type: Object as PropType<MenuItem> | any },
        category: String,
        shiftImg: Boolean,
        detail: Boolean,
        callback: [Function],
    },
    components: { IonItem, IonCard, IonImg, IonCol, IonRow, IonLabel },
    setup(props) {
        let path = '';
        if (props.item instanceof MenuItem) {
            path = props.category ? `/menu/${props.category}/${props.item?.path}` : `/menu/${props.item?.path}`;
        } else {
            path = props.category ? `/menu/${props.category}/${props.item?.path}` : props.item?.use_base_url ? `/${props.item?.path}` : `/menu/${props.item?.path}`;
        }

        const store = useStore();
        const route = useRoute();
        const isTablet = toRef(window, 'innerWidth');
        const isMobile = computed(() => store.getters.isMobile);
        // @ts-ignore
        const groupNumber = parseInt(route.params.groupNumber);

        const imageClass = computed(() => {
            const mobileView = isTablet.value > 768 && isTablet.value <= 1024 ? false : isMobile.value;
            const desktopView = isTablet.value > 768 && isTablet.value <= 1024 ? true : !mobileView;
            return {
                'image-ratio-mobile': mobileView,
                'image-ratio-desktop': desktopView,
                shiftImg: props.shiftImg,
            };
        });

        watchEffect(() => {
            const handleResize = () => {
                isTablet.value = window.innerWidth;
            };

            window.addEventListener('resize', handleResize);

            // Cleanup function
            return () => {
                window.removeEventListener('resize', handleResize);
            };
        });

        const itemClicked = () => {
            if (props.callback) {
                props.callback(props.item);
            }
        };

        return { path, isMobile, imageClass, createSlug, itemClicked, route, groupNumber };
    },
});
</script>

<style scoped>
.ion-activatable {
    -webkit-filter: brightness(100%);
    -webkit-transition: all 0.6s ease;
    -moz-transition: all 0.6s ease;
    -o-transition: all 0.6s ease;
    -ms-transition: all 0.6s ease;
    transition: all 0.6s ease;
}

.ion-activatable:hover {
    -webkit-filter: brightness(94%);
}

ion-item {
    --min-height: 70px;
}

.img-col {
    min-height: 70px;
}

.img-banner {
    z-index: 10;
    width: 100%;
    position: absolute;
    top: 0;
    background-color: var(--ion-color-primary);
    opacity: 0.85;
    text-align: center;
    color: white;
    font-weight: bold;
}

@media screen and (min-width: 720px) {
    ion-item {
        --min-height: 48px;
    }
}

@media screen and (min-width: 960px) {
    ion-img {
        object-fit: cover;
        min-height: 200px;
        min-width: 200px;

        max-height: 300px;
        max-width: 300px;
    }

    .item-detail-col {
        position: absolute;
        bottom: 0%;
    }

    ion-item {
        padding-bottom: 0;
    }

    .detailInfo,
    .detailInfo > ion-item {
        height: 65px;
        --min-height: 65px;
    }
}

@media screen and (min-width: 1600px) {
    ion-img {
        min-width: 100%;
        max-height: 565px;
    }
}

.image-ratio-desktop {
    aspect-ratio: 1/1;
    object-fit: cover;
    object-position: center center;
}

.image-ratio-mobile {
    aspect-ratio: 3/2;
    object-fit: cover;
    height: 100%;
    object-position: center center;
}
</style>
